import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import { draftId, draftSlug } from '../../../../routing/routeConfiguration';
import css from './CustomHero.module.css';

// Use SVG logos from public folder
const birdieChanceSingleLineUrl = '/static/logos/birdie-chance-single-line.svg';
const birdieChanceMobileUrl = '/static/logos/birdie-chance-mobile-hero.svg';

const CustomHero = ({ title, description, appearance }) => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const [logoSrc, setLogoSrc] = useState('');
  const [logoContent, setLogoContent] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isPortrait = screenHeight > screenWidth;
      
      // Mobile: width < 768px (always use mobile hero SVG)
      // Tablet portrait: width >= 768px && width <= 1024px && portrait orientation (use mobile hero SVG)
      // Tablet landscape: width >= 768px && width <= 1024px && landscape orientation (use single-line SVG)
      // Desktop: width > 1024px (use single-line SVG)
      const isMobileDevice = screenWidth < 768;
      const isTabletPortrait = screenWidth >= 768 && screenWidth <= 1024 && isPortrait;
      const shouldUseMobileLogo = isMobileDevice || isTabletPortrait;
      
      const logoSrc = shouldUseMobileLogo ? birdieChanceMobileUrl : birdieChanceSingleLineUrl;
      setLogoSrc(logoSrc);
      setIsMobile(screenWidth < 768);

      // Fetch and modify the SVG to use the correct fill color
      fetch(logoSrc)
        .then(response => response.text())
        .then(svgText => {
          // Parse and modify the SVG to use the correct fill color
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
          const svgElement = svgDoc.querySelector('svg');
          
          if (svgElement) {
            // Set SVG attributes for proper sizing
            svgElement.setAttribute('width', '100%');
            svgElement.removeAttribute('height'); // Remove height attribute to avoid "auto" error
            svgElement.style.height = 'auto';
            svgElement.style.maxHeight = isMobileDevice ? '180px' : '300px';
            
            // Set fill color on all elements that can have fill
            const fillableElements = svgElement.querySelectorAll('path, circle, rect, polygon, ellipse, g');
            fillableElements.forEach(element => {
              element.setAttribute('fill', '#FEFF20');
              element.setAttribute('stroke', 'none'); // Remove any strokes that might show as grey
            });
            
            // Also set the SVG root fill as fallback
            svgElement.setAttribute('fill', '#FEFF20');
            
            // Remove any existing fill attributes that might be overriding
            const allElements = svgElement.querySelectorAll('*');
            allElements.forEach(element => {
              if (element.hasAttribute('fill') && element.getAttribute('fill') !== '#FEFF20') {
                element.setAttribute('fill', '#FEFF20');
              }
              // Remove stroke that might appear grey
              if (element.hasAttribute('stroke')) {
                element.setAttribute('stroke', 'none');
              }
            });
            
            // Set the SVG content
            setLogoContent(svgElement.outerHTML);
          }
        })
        .catch(error => {
          console.error('Error loading SVG:', error);
          setLogoContent('');
        });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFindGearClick = () => {
    const searchPath = createResourceLocatorString('SearchPage', routeConfiguration, {}, { pub_listingType: 'sell-your-gear' });
    history.push(searchPath);
  };

  const handleSellGearClick = () => {
    const newListingPath = createResourceLocatorString(
      'EditListingPage',
      routeConfiguration,
      { slug: draftSlug, id: draftId, type: 'new', tab: 'details' },
      { listingType: 'sell-your-gear' }
    );
    history.push(newListingPath);
  };

  return (
    <div className={css.customHeroContent}>
      {/* Logo */}
      {logoContent && (
        <div className={css.customHeroLogo}>
          <div 
            dangerouslySetInnerHTML={{ __html: logoContent }}
            style={{
              maxWidth: isMobile ? '80vw' : '1000px',
              maxHeight: isMobile ? '180px' : '300px',
              width: '100%',
              height: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          />
        </div>
      )}

      {/* Title */}
      {title && (
        <h1 className={css.customHeroTitle}>
          {title.content || title}
        </h1>
      )}

      {/* Description */}
      {description && (
        <div className={css.customHeroDescription}>
          {description.content || description}
        </div>
      )}

      {/* Action Buttons */}
      <div className={css.customHeroButtons}>
        <button 
          className={`${css.customHeroButton} ${css.customHeroButtonPrimary}`}
          onClick={handleSellGearClick}
        >
          Sell Your Gear
        </button>
        <button 
          className={`${css.customHeroButton} ${css.customHeroButtonSecondary}`}
          onClick={handleFindGearClick}
        >
          Find Golf Gear
        </button>
      </div>

    </div>
  );
};

export default CustomHero;
